/**
 * Sign in.
 *
 * Rate-limit aware: a 429 carries the wait time from the server, and the form
 * disables itself and counts down rather than letting the user keep hammering
 * a locked endpoint and re-reading the same refusal.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { pluralize } from '@/lib/format'

export function LoginPage() {
  const { user, login, loading } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lockedFor, setLockedFor] = useState(0)

  // Count the lockout down so the form re-enables itself without a refresh.
  useEffect(() => {
    if (lockedFor <= 0) return
    const timer = setInterval(() => setLockedFor((n) => Math.max(0, n - 1)), 1000)
    return () => clearInterval(timer)
  }, [lockedFor])

  if (!loading && user) return <Navigate to="/" replace />

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await login(username.trim(), password)
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message)
        if (caught.status === 429 && caught.retryAfterSeconds) {
          setLockedFor(caught.retryAfterSeconds)
        }
      } else {
        setError('Something went wrong. Try again.')
      }
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  const locked = lockedFor > 0
  const disabled = busy || locked || username.trim() === '' || password === ''

  return (
    <div
      className="flex min-h-dvh items-center justify-center p-6"
      style={{
        background:
          'radial-gradient(900px 420px at 50% -10%, rgba(0,64,119,0.10) 0%, rgba(0,64,119,0) 62%), var(--color-surface-2)',
      }}
    >
      <div className="card w-full max-w-sm p-7">
        <div className="mb-6 flex items-center gap-2.5">
          <span
            aria-hidden
            className="flex size-9 items-center justify-center rounded-md bg-navy text-lg font-bold text-white"
          >
            &#10003;
          </span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-navy">Docket</h1>
            <p className="text-xs text-ink-3">Sign in to continue</p>
          </div>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <div>
            <label htmlFor="username" className="mb-1 block text-sm font-medium text-ink-2">
              Username
            </label>
            <input
              id="username"
              className="field"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              required
              disabled={locked}
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-ink-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="field"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={locked}
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-md bg-error-light px-3 py-2 text-sm text-error-text"
            >
              {error}
            </p>
          )}

          <button type="submit" className="btn btn-primary mt-1 w-full" disabled={disabled}>
            {busy
              ? 'Signing in...'
              : locked
                ? `Locked for ${lockedFor} ${pluralize(lockedFor, 'second')}`
                : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
